import "./PatientNote.scss";
import { DeleteIcon } from "../../assets/icons-components/Icons";
import AddIcon from "@mui/icons-material/Add";
import Buttons from "../Buttons/Buttons";
import { useEffect, useState } from "react";
import {
  AddPatientNote,
  DeletePatientNoteById,
  GetPatientAllNote,
} from "../../utils/apiCalls/patients";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import FormModal from "../FormModal/FormModal";
import Spinner from "../Spinner/Spinner";
import { useToast } from "../../utils/ToastContext";
import useConfirmDialog from "../../hooks/useConfirmDialog";

const formatDate = (date) => {
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return date.toLocaleString(undefined, options);
};

const PatientNote = ({ patientId }) => {
  const [addNote, setAddNote] = useState({ noteContent: "" });
  const [errors, setErrors] = useState({ addNote: "" });
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [noteData, setNoteData] = useState([]);
  const deleteDialog = useConfirmDialog();
  const [showInitialLoader, setShowInitialLoader] = useState(true);
  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddNote((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateField = () => {
    let errObj = { ...errors };

    if (!addNote.noteContent) {
      errObj.addNote = "This field is required";
    } else {
      errObj.addNote = "";
    }

    setErrors(errObj);
    const isValid = Object.values(errObj).every((x) => x === "" || x === null);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (validateField()) {
      const payload = {
        noteContent: addNote.noteContent,
        patientId: patientId,
      };
      try {
        const res = await AddPatientNote(payload);
        setIsLoading(false);
        const { statusCode, message } = res;
        if (statusCode === 200) {
          showToast("Patient Note Added Successfully", "success");
          fetchAllPatientNote(patientId);
          setAddNote({ noteContent: "" });
          setAddNoteModalOpen(false);
        } else {
          showToast(message, "error");
        }
      } catch (error) {
        setIsLoading(false);
        showToast(error?.message, "error");
      }
    } else {
      setIsLoading(false);
    }
  };

  const fetchAllPatientNote = async (id, isStale = () => false) => {
    try {
      const response = await GetPatientAllNote(id);
      if (isStale()) return;
      if (response.statusCode === 200) {
        const notes = response.data.map((note) => {
          const selectDate = new Date(note.createdDate);
          return { ...note, selectDate };
        });
        setNoteData(notes);
      } else {
        console.error("Error fetching professional details:", response.message);
      }
    } catch (error) {
      if (isStale()) return;
      console.error("Error fetching professional details:", error?.message);
    }
  };

  useEffect(() => {
    let ignore = false;
    // Reset per-patient state so switching patientId (even without this
    // component unmounting) never shows the previous patient's notes/draft.
    setNoteData([]);
    setAddNote({ noteContent: "" });
    setErrors({ addNote: "" });
    setShowInitialLoader(true);
    fetchAllPatientNote(patientId, () => ignore);
    // Show loader for 1 second when popup opens
    const loaderTimer = setTimeout(() => {
      setShowInitialLoader(false);
    }, 1000);
    return () => {
      ignore = true;
      clearTimeout(loaderTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const handleDelete = (note) => deleteDialog.openDialog(note);

  const confirmDelete = async () => {
    const note = deleteDialog.target;
    const noteId = note?.noteId || note;
    try {
      const res = await DeletePatientNoteById(noteId);
      const { statusCode, message } = res;
      if (statusCode === 200) {
        showToast("Patient Note Deleted Successfully", "success");
        fetchAllPatientNote(patientId);
      } else {
        showToast(message, "error");
      }
    } catch (error) {
      showToast(error?.message, "error");
    }
    deleteDialog.closeDialog();
  };

  const openAddNoteModal = () => {
    setAddNote({ noteContent: "" });
    setErrors({ addNote: "" });
    setAddNoteModalOpen(true);
  };

  const closeAddNoteModal = () => {
    setAddNoteModalOpen(false);
    setAddNote({ noteContent: "" });
    setErrors({ addNote: "" });
  };

  return (
    <div className="note">
      {(isLoading || showInitialLoader) && (
        <div className="loader-container">
          <div className="loader">
            <Spinner />
          </div>
        </div>
      )}
      <div className="note_container">
        <div className="note_container_section">
          <div className="tab-panel-header">
            <h3>Notes</h3>
            <Buttons
              text="Add Note"
              type="button"
              variant="primary"
              fullWidth={false}
              onClick={openAddNoteModal}
              icon={<AddIcon fontSize="small" />}
            />
          </div>
          <div className="note_container_section_title">
            <h4>Previous Notes</h4>
          </div>
          {noteData.length === 0 && !showInitialLoader && (
            <p className="note_container_section_empty">
              No notes yet — click "Add Note" to create one.
            </p>
          )}
          {noteData.map((data) => (
            <div className="note_container_section_box" key={data.noteId}>
              <div className="note_container_section_box_one">
                <p>{data.noteContent}</p>
              </div>
              <div className="note_container_section_box_two">
                <div className="note_container_section_box_two_date">
                  <p>{formatDate(data.selectDate)}</p>
                </div>
                <button
                  type="button"
                  className="note_container_section_box_two_icon"
                  onClick={() => handleDelete(data)}
                  aria-label="Delete note"
                >
                  <DeleteIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {addNoteModalOpen && (
        <FormModal
          open={addNoteModalOpen}
          onClose={closeAddNoteModal}
          title="Add Note"
          actions={
            <>
              <Buttons text="Cancel" type="button" variant="secondary" fullWidth={false} onClick={closeAddNoteModal} />
              <Buttons text="Save" type="button" variant="primary" fullWidth={false} onClick={handleSubmit} icon={<AddIcon />} />
            </>
          }
        >
          <div className="note_container_section_textarea">
            <label htmlFor="textarea">Notes</label>
            <textarea
              id="textarea"
              name="noteContent"
              type="text"
              value={addNote.noteContent}
              rows={5}
              placeholder="Add Note"
              onChange={handleChange}
              autoFocus
            ></textarea>
            {errors.addNote && <span className="error">{errors.addNote}</span>}
          </div>
        </FormModal>
      )}

      {deleteDialog.open && (
        <ConfirmDialog
          open={deleteDialog.open}
          onClose={deleteDialog.closeDialog}
          title="Delete Patient Note"
          severity="error"
          confirmText="DELETE"
          content={
            <span>
              Are you sure you want to delete note: <strong>"{deleteDialog.target?.noteContent?.substring(0, 40)}{deleteDialog.target?.noteContent?.length > 40 ? "..." : ""}"</strong>?
            </span>
          }
          buttonText="Yes, Delete"
          additionalButtonText="Cancel"
          onButtonClick={confirmDelete}
          onAdditionalButtonClick={deleteDialog.closeDialog}
        />
      )}
    </div>
  );
};

export default PatientNote;
